import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { money, unitPrice } from "@/lib/api";
import { BulkHint } from "@/components/store/BulkDeal";

const QtyInput = ({ item, setQty }) => {
  const [v, setV] = useState(String(item.qty));
  useEffect(() => setV(String(item.qty)), [item.qty]);
  const commit = () => { const n = parseInt(v, 10); if (n > 0) setQty(item.id, n); else setV(String(item.qty)); };
  return (
    <input data-testid={`cart-qty-${item.id}`} type="number" inputMode="numeric" min={1} value={v}
      onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className="font-mono text-sm w-14 text-center bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none" />
  );
};

export const CartSheet = () => {
  const { items, setQty, remove, total, open, setOpen } = useCart();
  const navigate = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" className="bg-[#0F1117] border-white/10 text-white flex flex-col w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-display text-white tracking-tight">Your cart</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto mt-6 space-y-3">
          {items.length === 0 && (
            <div data-testid="cart-empty-state" className="text-center text-gray-400 py-16">
              <ShoppingBag className="mx-auto mb-3 opacity-40" /> Your cart is empty.
            </div>
          )}
          {items.map((i) => (
            <div key={i.id} data-testid={`cart-item-${i.id}`} className="p-4 rounded-md bg-[#161922] border border-white/5">
              <div className="flex justify-between gap-3">
                <div>
                  <p className="font-semibold leading-tight">{i.name}</p>
                  <p className="text-xs text-gray-400 mt-1">{i.kind === "box" ? `${i.codes} random codes` : `${i.platform} key`}</p>
                  <BulkHint item={i} />
                </div>
                <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} className="text-gray-500 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center gap-1 border border-white/10 rounded-md">
                  <button data-testid={`cart-decrease-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="p-2 hover:text-emerald-400"><Minus size={14} /></button>
                  <QtyInput item={i} setQty={setQty} />
                  <button data-testid={`cart-increase-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="p-2 hover:text-emerald-400"><Plus size={14} /></button>
                </div>
                <span className="font-mono text-emerald-400">{money(unitPrice(i, i.qty) * i.qty)}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 pt-4 space-y-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Total (USDT)</span>
            <span data-testid="cart-total" className="font-mono text-lg font-bold">{money(total)}</span>
          </div>
          <button
            data-testid="cart-checkout-button"
            disabled={!items.length}
            onClick={() => { setOpen(false); navigate("/checkout"); }}
            className="w-full h-12 rounded-md bg-emerald-500 text-[#08090C] font-bold hover:bg-emerald-400 disabled:opacity-40 transition-colors"
          >
            Pay with crypto
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
